import React, {useCallback, useEffect, useMemo} from "react";
import {useAuthStore} from "../../redux/auth";
import {useHistory} from "react-router";
import {apiCacheDuration, apiDomain, apiPrefix} from "../../services.config";
import {camelizeKeys} from "humps";
import apiClient from "../../services/api/apiClient";
import {
    applyCacheResponsePolicy,
    getCache,
    hasValidCache,
    markServedFromCache,
    shouldIncludeInCache,
} from "../../services/api/responseCache";

// TODO: Cache Duration only uses default
const DEFAULT_CACHE_DURATION_MS = apiCacheDuration ; // 30 seconds

interface ApiConfAttributes {
    setMustLogout: (newMustLogout: boolean) => void;
}

// Mirror axios' baseURL+url joining so we can reconstruct a request URL when the
// XHR `responseURL` is unavailable.
const combineUrls = (base?: string, url?: string): string | undefined => {
    if (!url) return base;
    if (/^https?:\/\//i.test(url)) return url;
    if (!base) return url;
    return `${base.replace(/\/+$/, "")}/${url.replace(/^\/+/, "")}`;
};

// Derive the request URL for a failed response, preferring the XHR `responseURL`
// and falling back to the request config. Returns null (never throws) if neither
// yields a parseable URL, so the 401 handler can't be swallowed by a bad parse.
const parseRequestUrl = (error: any): URL | null => {
    const raw = error?.request?.responseURL || combineUrls(error?.config?.baseURL, error?.config?.url);
    if (!raw) return null;
    try {
        return new URL(raw);
    } catch {
        return null;
    }
};

const ApiConfigurator: React.FC<ApiConfAttributes> = (props) => {
    const {setMustLogout} = props;
    const authStore = useAuthStore();
    const {isAuthed, apiAuth} = authStore;
    const history = useHistory();

    const logoutUrlPath = `${apiPrefix}/auth/logout`;
    const loginUrlPath = `${apiPrefix}/auth/login`;
    const ignored401 = useMemo(() => ([logoutUrlPath, loginUrlPath]), [logoutUrlPath, loginUrlPath]);

    const contentType = "application/json";

    // Setup Auth API Headers
    useEffect(() => {
        if (isAuthed && apiAuth && apiAuth.tokenType && apiAuth.token) {
            apiClient.defaults.headers.common["Authorization"] = `${apiAuth.tokenType} ${apiAuth.token}`;
        } else {
            // Without this, requests after logout — including the next user's
            // login — keep sending the previous user's bearer token (F63).
            delete apiClient.defaults.headers.common["Authorization"];
        }
    }, [apiAuth, isAuthed]);

    const setApiClientDefaults = useCallback(() => {
        const camelizeId = apiClient.interceptors.response.use((response) => {
            if (response.data && response.headers["content-type"] === contentType) {
                response.data = camelizeKeys(response.data);
            }
            return response;
        });

        const authId = apiClient.interceptors.response.use(
            function (response) {
                return response;
            },
            function (error) {
                if (error?.response?.status === 401) {
                    // `responseURL` is absent on non-XHR adapters / some WebView
                    // edges; fall back to the request config, and never let a
                    // parse failure swallow the 401 so auto-logout still fires (F64).
                    const errUrl = parseRequestUrl(error);
                    const path = errUrl?.pathname;
                    const sameOrigin = !errUrl || apiDomain === errUrl.origin;
                    if (sameOrigin && !(path && ignored401.includes(path))) {
                        setMustLogout(true);
                    }
                    if (sameOrigin && path === logoutUrlPath) {
                        if (history.location.pathname !== loginUrlPath) {
                            history.replace("/auth/login");
                        }
                    }
                }

                return Promise.reject(error);
            }
        );

        const cacheReqId = apiClient.interceptors.request.use((config) => {
            if (config.method === "get"
                && shouldIncludeInCache(config.url as string)
                && hasValidCache(config.url as string, DEFAULT_CACHE_DURATION_MS)) {
                // Tag the config so the response policy knows this was a cache HIT
                // and won't re-stamp the entry (keeps the TTL absolute, not sliding).
                markServedFromCache(config);
                config.adapter = async () => {
                    return new Promise((resolve) => {
                        resolve({
                            data: getCache(config.url as string),
                            status: 200,
                            statusText: "OK (cached)",
                            headers: {},
                            config,
                        });
                    });
                };
            }
            return config;
        });

        const cacheRespId = apiClient.interceptors.response.use((response) => {
            applyCacheResponsePolicy(response);
            return response;
        });

        // Eject exactly what we registered so a remount (React StrictMode double
        // -invokes effects) doesn't stack duplicate interceptors (F65).
        return () => {
            apiClient.interceptors.response.eject(camelizeId);
            apiClient.interceptors.response.eject(authId);
            apiClient.interceptors.request.eject(cacheReqId);
            apiClient.interceptors.response.eject(cacheRespId);
        };
    }, [history, loginUrlPath, logoutUrlPath, ignored401, setMustLogout])

    // Get the Device Timezone
    // Setup Generic API Headers and middleware
    useEffect(() => {
        return setApiClientDefaults();
    }, [setApiClientDefaults]);

    return null;
};

export default ApiConfigurator;
